// react_frontend/src/Components/Modules/addCompany.js
import React, { useState } from "react";
import axios from "axios";
import Constant from "../../Constant";
import authBg from "../../Assets/Media/authBg.webp";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import kora from "../../Assets/Media/lumen-logo.svg";
import koraFav from "../../Assets/Media/lumen-logo-white.svg";


// Icons
import {
  FaHome, FaBuilding, FaEnvelope, FaPhone, FaMapMarkerAlt,
  FaCopy, FaChartLine, FaUsers, FaShieldAlt,
} from "react-icons/fa";

const AddCompany = () => {
  const navigator = useNavigate();
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    company_email: "",
    company_phone: "",
    company_address: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setLicenseInfo(null);

    try {
      const res = await axios.post(`${Constant.BASE_URL}/company-register`, formData);
      const data = res.data;

      if (data.status === true) {
        localStorage.setItem("setup_complete", "true");
        localStorage.setItem("company_email", data.company_email);
        localStorage.setItem("company_name", data.company_name);

        setLicenseInfo({
          license_key: data.license_key,
          trial_start: data.trial_start,
          trial_end: data.trial_end,
          password: data.password,
        });

        setIsLoading(false);

        Swal.fire({
          title: "Success!",
          text: "Company registered successfully! Please check your email for password to login.",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#6366f1",
        });

        navigator("/");
      }
    } catch (error) {
      if (error.response?.status === 422) {
        setErrors(error.response.data.errors || {});
      } else {
        setErrors({ general: "Something went wrong. Please try again." });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        Swal.fire({
          title: "Copied!",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      })
      .catch(() => {
        Swal.fire({ title: "Error", text: "Failed to copy.", icon: "error", confirmButtonText: "OK" });
      });
  };

  return (
    <div className="auth-page">
      {/* ===== Left: Branding panel (hidden on mobile) ===== */}
      <div className="auth-brand" style={{ backgroundImage: `url(${authBg})` }}>
        <div className="auth-brand-overlay" />
        <div className="auth-brand-content">
          <div className="auth-logo-badge">
            <img src={koraFav} alt="Kora Logo" className="auth-logo-img" />
          </div>
          <h1>Lumen</h1>
          <p className="auth-tagline">
            Start your business journey with a complete Enterprise solution.
          </p>
          <ul className="auth-features">
            <li><FaChartLine /> Real-time sales & inventory insights</li>
            <li><FaUsers /> Full HR, payroll & attendance management</li>
            <li><FaShieldAlt /> Licensed, secure & supported</li>
          </ul>
        </div>
      </div>

      {/* ===== Right: Form panel ===== */}
      <div className="auth-form-side">
        <div className="auth-card">
          <div className="auth-card-header">
            <h4>Set Up Your Company</h4>
            <p>Enter your business details to get started</p>
          </div>

          {errors.general && <div className="auth-alert danger">{errors.general}</div>}

          <form onSubmit={handleSubmit}>
            <div className="auth-field">
              <label htmlFor="company_name"><FaBuilding /> Company Name *</label>
              <input
                id="company_name"
                type="text"
                name="company_name"
                placeholder="Enter your company name"
                value={formData.company_name}
                onChange={handleChange}
                required
              />
              {errors.company_name && <span className="auth-error">{errors.company_name[0]}</span>}
            </div>

            <div className="auth-field">
              <label htmlFor="company_email"><FaEnvelope /> Email *</label>
              <input
                id="company_email"
                type="email"
                name="company_email"
                placeholder="company@email.com"
                value={formData.company_email}
                onChange={handleChange}
                required
              />
              <span className="auth-hint">
                👉 Login credentials and all information will be sent to this primary email.
              </span>
              {errors.company_email && <span className="auth-error">{errors.company_email[0]}</span>}
            </div>

            <div className="auth-field">
              <label htmlFor="company_phone"><FaPhone /> Phone</label>
              <input
                id="company_phone"
                type="text"
                name="company_phone"
                placeholder="Enter your phone number"
                value={formData.company_phone}
                onChange={handleChange}
              />
              {errors.company_phone && <span className="auth-error">{errors.company_phone[0]}</span>}
            </div>

            <div className="auth-field">
              <label htmlFor="company_address"><FaMapMarkerAlt /> Address *</label>
              <textarea
                id="company_address"
                name="company_address"
                placeholder="Enter your company address"
                value={formData.company_address}
                onChange={handleChange}
                rows="3"
                required
              />
              {errors.company_address && <span className="auth-error">{errors.company_address[0]}</span>}
            </div>

            <button type="submit" className="auth-submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" /> Processing...
                </>
              ) : (
                "Register & Continue"
              )}
            </button>
          </form>

          <div className="auth-back">
            <Link to="/">
              <FaHome /> Back to Login
            </Link>
          </div>

          {licenseInfo && (
            <div className="auth-license">
              <h5>🎉 Company Registered Successfully!</h5>

              <div className="auth-license-row">
                <span><strong>License Key:</strong> {licenseInfo.license_key}</span>
                <button type="button" className="auth-copy-btn" onClick={() => copyToClipboard(licenseInfo.license_key)}>
                  <FaCopy /> Copy
                </button>
              </div>

              <div className="auth-license-row">
                <span><strong>Trial Start:</strong> {new Date(licenseInfo.trial_start).toLocaleDateString()}</span>
              </div>

              <div className="auth-license-row">
                <span><strong>Trial End:</strong> {new Date(licenseInfo.trial_end).toLocaleDateString()}</span>
              </div>

              <div className="auth-license-row">
                <span><strong>Password:</strong> {licenseInfo.password}</span>
                <button type="button" className="auth-copy-btn" onClick={() => copyToClipboard(licenseInfo.password)}>
                  <FaCopy /> Copy
                </button>
              </div>

              <p className="auth-hint" style={{ marginTop: 10 }}>
                Please check your email for full login credentials and license details.
              </p>
            </div>
          )}
        </div>

        <footer className="auth-footer">
          © SSM-Lumen {new Date().getFullYear()} | Powered by Soft Solution Media
        </footer>
      </div>
    </div>
  );
};

export default AddCompany;